/**
 * Application Configuration
 * Central location for all app-wide constants and configuration
 */

export const CONFIG = {
  // Supabase Configuration
  SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL || 'https://lkwtufmxdmfcqhesovvn.supabase.co',

  // Video Preloading Configuration
  PRELOAD_COUNT: 8,                  // Number of videos to preload ahead
  PARALLEL_PRELOAD_COUNT: 5,         // Number of videos to fetch in parallel
  PRELOAD_STARTUP_DELAY: 0,          // Delay before starting preload (ms)

  // Presigned URL Configuration
  PRESIGNED_URL_EXPIRY: 28800,       // 8 hours in seconds
  PRESIGNED_URL_REFRESH_INTERVAL: 60000, // Check every 60 seconds
  PRESIGNED_URL_REFRESH_AT: 14400,   // Refresh at 4 hours (50% of lifetime)

  // Edge Function Endpoints
  EDGE_FUNCTIONS: {
    SERVE_MEDIA: '/functions/v1/serve-media',
  },
} as const;

/**
 * Edge Function Context Configuration
 *
 * Context parameter controls HTTP Range Request behavior:
 * - 'player': Enables Range Requests for cropped videos (60-80% bandwidth savings)
 * - 'editor': Disables Range Requests for scrubbing (needs full video)
 * - 'thumbnail': Serves thumbnail image (deterministic path from storage_path)
 */
export const EDGE_FUNCTION_CONTEXT = {
  PLAYER: 'player' as const,      // For video playback (uses Range Requests)
  EDITOR: 'editor' as const,      // For crop editor (downloads full video)
  THUMBNAIL: 'thumbnail' as const, // For thumbnail images
} as const;

export type EdgeFunctionContext = typeof EDGE_FUNCTION_CONTEXT[keyof typeof EDGE_FUNCTION_CONTEXT];

/**
 * Helper function to get Supabase URL
 * Encapsulates the config access for better testability
 */
export function getSupabaseUrl(): string {
  return CONFIG.SUPABASE_URL;
}

/**
 * Build Edge Function URL for serving media
 * @param mediaId - Media ID or template path (e.g., "template-music/Song.mp3")
 * @param context - Context for Range Request handling
 * @returns Full Edge Function URL
 */
export function getMediaApiUrl(mediaId: string, context?: EdgeFunctionContext): string {
  const baseUrl = `${CONFIG.SUPABASE_URL}${CONFIG.EDGE_FUNCTIONS.SERVE_MEDIA}/${mediaId}`;
  return context ? `${baseUrl}?context=${context}` : baseUrl;
}

/**
 * Response from presigned URL fetch
 */
export interface PresignedUrlResponse {
  url: string;           // R2 presigned URL (optimized file if available)
  generatedAt: number;   // Timestamp when URL was generated (ms)
  expiresIn: number;     // Lifetime in seconds (default 28800 = 8h)
  range?: string;        // Optional HTTP Range header (e.g., "bytes=1000000-3000000")
  thumbnailSize?: number | null; // Decision 2B — derived thumbnail byte size for
                                 // data-usage attribution (cross-origin TAO gap)
}

/**
 * Fetch a single presigned URL from the Edge Function.
 * Delegates to fetchMediaUrlBatch under the hood — one implementation path.
 *
 * @param mediaId - Video/photo/music ID or template path
 * @param context - Context for Range Request handling ('player' or 'editor')
 * @param abortSignal - Optional AbortSignal for canceling the request
 * @returns Promise with presigned URL data
 */
export async function fetchMediaUrl(
  mediaId: string,
  context?: EdgeFunctionContext,
  abortSignal?: AbortSignal
): Promise<PresignedUrlResponse> {
  const results = await fetchMediaUrlBatch(
    [{ id: mediaId, context }],
    abortSignal
  );
  const result = results.get(mediaId);
  if (!result?.url) {
    throw new Error(result?.error || `No presigned URL returned for ${mediaId.slice(0, 8)}`);
  }
  return {
    url: result.url,
    generatedAt: result.generatedAt,
    expiresIn: result.expiresIn,
    range: result.range ?? undefined,
    thumbnailSize: result.thumbnailSize ?? null,
  };
}

/**
 * Batch request for presigned URLs
 */
export interface BatchPresignedUrlRequest {
  id: string;
  context?: EdgeFunctionContext;
}

/**
 * Batch response for presigned URLs
 */
export interface BatchPresignedUrlResponse {
  id: string;
  url: string | null;
  range: string | null;
  expiresIn: number;
  generatedAt: number;
  // Decision 2B — sizes of derived optimized files (thumbnail / cropped_mini)
  // on R2, used for data-usage attribution since the browser can't read
  // transferSize cross-origin without TAO. Null for older uploads.
  thumbnailSize?: number | null;
  croppedMiniSize?: number | null;
  error?: string;
}

/**
 * Fetch presigned URLs in batch from Edge Function.
 * Single implementation path for all media URL fetching (fetchMediaUrl delegates here).
 *
 * @param requests - Array of media IDs and contexts to fetch
 * @param abortSignal - Optional AbortSignal for canceling the request
 * @returns Promise with map of media IDs to presigned URL responses
 */
export async function fetchMediaUrlBatch(
  requests: BatchPresignedUrlRequest[],
  abortSignal?: AbortSignal
): Promise<Map<string, BatchPresignedUrlResponse>> {
  const MAX_RETRIES = 3;
  const BASE_DELAY_MS = 1000;

  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      // Dynamic import to avoid circular dependency
      const { supabase } = await import('@/integrations/supabase/client');

      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      // Build Edge Function URL
      const batchUrl = `${CONFIG.SUPABASE_URL}/functions/v1/serve-media-batch`;

      // Fetch from Edge Function
      const response = await fetch(batchUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ requests }),
        signal: abortSignal
      });

      if (!response.ok) {
        const isRetryable = response.status === 429 || response.status >= 500;
        if (isRetryable && attempt < MAX_RETRIES) {
          const delay = BASE_DELAY_MS * Math.pow(2, attempt - 1);
          console.warn(`[fetchMediaUrlBatch] ⚠️ ${response.status}, retrying in ${delay}ms (attempt ${attempt}/${MAX_RETRIES})`);
          await new Promise(r => setTimeout(r, delay));
          continue;
        }
        console.error(`[fetchMediaUrlBatch] ❌ HTTP error: ${response.status}`);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Parse JSON response
      const data = await response.json();

      if (!data.responses || !Array.isArray(data.responses)) {
        throw new Error('Invalid batch response from Edge Function');
      }

      // Convert array to map for easy lookup
      const resultMap = new Map<string, BatchPresignedUrlResponse>();
      data.responses.forEach((response: BatchPresignedUrlResponse) => {
        resultMap.set(response.id, response);
      });

      return resultMap;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Don't retry aborted requests
      if (abortSignal?.aborted) throw lastError;

      const isNetworkError = lastError.message.includes('Failed to fetch') ||
        lastError.message.includes('NetworkError') ||
        lastError.message.includes('ERR_SSL');

      if (isNetworkError && attempt < MAX_RETRIES) {
        const delay = BASE_DELAY_MS * Math.pow(2, attempt - 1);
        console.warn(`[fetchMediaUrlBatch] ⚠️ Network error, retrying in ${delay}ms (attempt ${attempt}/${MAX_RETRIES})`);
        await new Promise(r => setTimeout(r, delay));
        continue;
      }

      throw lastError;
    }
  }

  throw lastError!;
}
